import React, { useEffect, useState } from 'react'
import styles from './index.module.css'
import Header from '../components/header'
import RoundedButton from '../components/roundedButton'
import { Search } from '@mui/icons-material'
import RoundedInput from '../components/roundedInput'
import { Modal } from '@mui/material'
import NewUserModal from '../components/newUserModal'
import ApiService from '../ApiService'
import { UserResponse } from '../models'
import EditIcon from '@mui/icons-material/Edit'
import Loading from '../components/loading'

export default function UsersPage() {
  const [newUserModal, setNewUserModal] = useState<boolean>(false)
  const [users, setUsers] = useState<UserResponse[]>([])
  const [selectedUser, setSelectedUser] = useState<UserResponse>()
  const [page, setPage] = useState<number>(1)
  const [lastFetched, setLastFetched] = useState<number>()
  const [fetching, setFetching] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const tableColumns = [
    { key: 'firstName', label: 'Nome', className: styles.nameColumn },
    { key: 'lastName', label: 'Sobrenome', className: styles.lastNameColumn },
    { key: 'email', label: 'E-mail', className: styles.emailColumn },
    { key: 'phone', label: 'Telefone', className: styles.phoneColumn },
    { key: 'profile', label: 'Perfil', className: styles.profileColumn },
    { key: 'actions', label: 'Ações', className: styles.actionsColumn },
  ]

  const getUserList = async (pageNumber: number) => {
    setIsLoading(true)
    try {
      const data = await ApiService.getUserById('limit100', pageNumber) as UserResponse[]
      setUsers((currentUsers) => pageNumber !== 1 ? [...currentUsers, ...data] : data)
      setLastFetched(data.length)
    } catch (error: any) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = async (term: string) => {
    const trimmedSearchTerm = term.trim()
    const encodedSearchTerm = encodeURIComponent(trimmedSearchTerm)
    setSearchTerm(trimmedSearchTerm)

    try {
      if (trimmedSearchTerm.length >= 4) {
        const response = await ApiService.getUserBySearchTerm(encodedSearchTerm)
        const userList = response.data as UserResponse[]
        setUsers(userList)
        setPage(1)
        setLastFetched(userList.length)
        return
      }

      if (!trimmedSearchTerm) {
        setPage(1)
        await getUserList(1)
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        console.error('Error searching users:', error)
      }
    }
  }

  const refreshUsers = async () => {
    if (searchTerm && searchTerm.length >= 4) {
      await handleSearch(searchTerm)
      return
    }

    setPage(1)
    await getUserList(1)
  }

  const handleScroll = async (e: any) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target
    const bufferHeight = 50

    if (
      Math.floor(scrollTop + clientHeight) >= Math.floor(scrollHeight) - bufferHeight &&
      lastFetched &&
      lastFetched >= 100 &&
      !fetching &&
      searchTerm === ''
    ) {
      setFetching(true)
      await getUserList(page + 1)
      setPage((currentPage) => currentPage + 1)
      setFetching(false)
    }
  }

  useEffect(() => {
    void getUserList(1)
  }, [])

  return (
    <div className={styles.background}>
      <Modal open={newUserModal} onClose={() => setNewUserModal(false)}>
        <NewUserModal
          closeCallback={() => setNewUserModal(false)}
          userToEdit={selectedUser}
          onSuccessCallback={refreshUsers}
        />
      </Modal>
      <Header />
      <p className={styles.pageTitle}>Lista de Usuários</p>
      <div className={styles.menuRow}>
        <div className={styles.newUserButton}>
          <RoundedButton
            buttonText='Novo Usuário'
            buttonType='button'
            onClickCallback={() => {
              setSelectedUser(undefined)
              setNewUserModal(true)
            }}
          />
        </div>
        <div className={styles.searchUserInput}>
          <Search />
          <RoundedInput transparent disableLabel label='Pesquisar Usuário' type='text' onChangeCallback={handleSearch} />
        </div>
      </div>
      <div className={styles.tableShell}>
        <div className={styles.tableViewport} onScroll={handleScroll}>
          <table className={styles.table}>
            <thead className={styles.tableHead}>
              <tr className={styles.headerRow}>
                {tableColumns.map((column) => (
                  <th key={column.key} className={column.className}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {users.map((user) => (
                <tr key={user.id} className={styles.dataRow}>
                  <td className={styles.nameColumn} data-label='Nome'>
                    <span className={styles.primaryText}>{user.first_name}</span>
                  </td>
                  <td className={styles.lastNameColumn} data-label='Sobrenome'>
                    <span className={styles.primaryText}>{user.last_name}</span>
                  </td>
                  <td className={styles.emailColumn} data-label='E-mail'>
                    <span className={styles.primaryText}>{user.email}</span>
                  </td>
                  <td className={styles.phoneColumn} data-label='Telefone'>
                    <span className={styles.primaryText}>{Number(user.phone) !== 0 ? user.phone : ''}</span>
                  </td>
                  <td className={styles.profileColumn} data-label='Perfil'>
                    <span className={styles.profileBadge}>{user.account_type}</span>
                  </td>
                  <td className={styles.actionsColumn} data-label='Ações'>
                    <div className={styles.actionsContainer}>
                      <button
                        type='button'
                        className={styles.actionButton}
                        onClick={() => {
                          setSelectedUser(user)
                          setNewUserModal(true)
                        }}
                      >
                        <EditIcon fontSize='small' />
                        <span>Editar</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {isLoading && (
            <div className={styles.loadingContainer}>
              <Loading />
            </div>
          )}
          {!isLoading && users.length === 0 && (
            <div className={styles.emptyState}>
              Nenhum usuário encontrado para os filtros atuais.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
